import { Theme, css } from '@emotion/react';

import { useEffect, useRef, useState } from 'react';

declare global {
  interface Window {
    adsbygoogle?: Array<Record<string, unknown>>;
  }
}

const containerCSS = (theme: Theme) => css`
  font-size: 0;
  height: 0;
  overflow: hidden;

  &.adsense-loaded {
    height: auto;
    border-top: 8px solid ${theme.border.space};
    border-bottom: 8px solid ${theme.border.space};
  }

  &.adsense-failed {
    display: none;
  }
`;

const adLabelCSS = (theme: Theme) => css`
  font-size: 11px;
  color: ${theme.var.gray400};
  padding: 4px 12px 0;
  line-height: 1;
`;

const devPreviewCSS = (theme: Theme) => css`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  background: ${theme.var.gray50};
  border: 1px dashed ${theme.var.gray300};
  border-radius: 8px;
  margin: 4px 0;

  .dev-ad-icon {
    width: 48px;
    height: 48px;
    border-radius: 8px;
    background: linear-gradient(
      135deg,
      ${theme.var.gray200},
      ${theme.var.gray300}
    );
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
  }

  .dev-ad-content {
    flex: 1;
    min-width: 0;
  }

  .dev-ad-label {
    font-size: 10px;
    color: ${theme.var.gray400};
    margin-bottom: 2px;
  }

  .dev-ad-title {
    font-size: 14px;
    font-weight: 600;
    color: ${theme.var.gray700};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .dev-ad-body {
    font-size: 12px;
    color: ${theme.var.gray500};
    margin-top: 2px;
  }

  .dev-ad-cta {
    font-size: 11px;
    font-weight: 600;
    color: white;
    background: ${theme.colors.main500};
    border: none;
    border-radius: 14px;
    padding: 6px 14px;
    flex-shrink: 0;
  }
`;

// AdSense 광고 단위 정의: {page}_{variant}_{platform}
type AdUnit = { slot: string; layoutKey: string };

const AD_UNITS: Record<string, AdUnit | undefined> = {
  home_cp_mobile: {
    slot: process.env.NEXT_PUBLIC_ADSENSE_HOME_CP_MOBILE_SLOT || '',
    layoutKey: process.env.NEXT_PUBLIC_ADSENSE_HOME_CP_MOBILE_LAYOUT_KEY || '',
  },
  home_sr_mobile: {
    slot: process.env.NEXT_PUBLIC_ADSENSE_HOME_SR_MOBILE_SLOT || '',
    layoutKey: process.env.NEXT_PUBLIC_ADSENSE_HOME_SR_MOBILE_LAYOUT_KEY || '',
  },
  feed_default_mobile: {
    slot: process.env.NEXT_PUBLIC_ADSENSE_FEED_DEFAULT_MOBILE_SLOT || '',
    layoutKey:
      process.env.NEXT_PUBLIC_ADSENSE_FEED_DEFAULT_MOBILE_LAYOUT_KEY || '',
  },
  library_mobile: {
    slot: process.env.NEXT_PUBLIC_ADSENSE_LIBRARY_MOBILE_SLOT || '',
    layoutKey: process.env.NEXT_PUBLIC_ADSENSE_LIBRARY_MOBILE_LAYOUT_KEY || '',
  },
  story_mobile: {
    slot: process.env.NEXT_PUBLIC_ADSENSE_STORY_MOBILE_SLOT || '',
    layoutKey: process.env.NEXT_PUBLIC_ADSENSE_STORY_MOBILE_LAYOUT_KEY || '',
  },
  episode_mobile: {
    slot: process.env.NEXT_PUBLIC_ADSENSE_EPISODE_MOBILE_SLOT || '',
    layoutKey: process.env.NEXT_PUBLIC_ADSENSE_EPISODE_MOBILE_LAYOUT_KEY || '',
  },
};

const ADSENSE_CLIENT = process.env.NEXT_PUBLIC_ADSENSE_CLIENT_ID;
const IS_DEV =
  !!ADSENSE_CLIENT &&
  typeof window !== 'undefined' &&
  window.location.hostname === 'localhost';

// 스크립트 중복 삽입 방지용 모듈 스코프 플래그
let adsenseScriptState: 'idle' | 'loading' | 'loaded' = 'idle';
const adsenseLoadCallbacks: Array<() => void> = [];

type NativeInFeedAdProps = {
  /** AdSense 광고 단위 키: home_cp_mobile, home_sr_mobile 등 */
  unitKey: string;
};

const NativeInFeedAd = ({ unitKey }: NativeInFeedAdProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const insRef = useRef<HTMLModElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const pushedRef = useRef(false);

  const adUnit = AD_UNITS[unitKey];
  const isEnabled = !!(ADSENSE_CLIENT && adUnit?.slot);

  // IntersectionObserver: 뷰포트 200px 이내 진입 시 로드
  useEffect(() => {
    if ((!isEnabled && !IS_DEV) || !containerRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [isEnabled]);

  // AdSense 스크립트 로드 + push
  useEffect(() => {
    if (!isEnabled || !isVisible || pushedRef.current || IS_DEV) return;

    const pushAd = () => {
      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
        pushedRef.current = true;
      } catch (e) {
        console.warn('[AdSense InFeed] push error:', e);
      }
    };

    if (adsenseScriptState === 'loaded') {
      pushAd();
      return;
    }

    if (adsenseScriptState === 'loading') {
      adsenseLoadCallbacks.push(pushAd);
      return;
    }

    // 최초 1회 스크립트 삽입
    adsenseScriptState = 'loading';
    adsenseLoadCallbacks.push(pushAd);

    const script = document.createElement('script');
    script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_CLIENT}`;
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.onload = () => {
      adsenseScriptState = 'loaded';
      adsenseLoadCallbacks.forEach((cb) => cb());
      adsenseLoadCallbacks.length = 0;
    };
    script.onerror = () => {
      adsenseScriptState = 'idle';
      adsenseLoadCallbacks.length = 0;
      console.warn('[AdSense InFeed] script load failed');
    };
    document.head.appendChild(script);
  }, [isEnabled, isVisible]);

  // 로드 완료 감지 + 타임아웃 시 숨김
  useEffect(() => {
    if (!insRef.current || !isVisible || IS_DEV) return;

    let loaded = false;

    const observer = new MutationObserver(() => {
      if (insRef.current?.getAttribute('data-ad-status') === 'unfilled') {
        containerRef.current?.classList.remove('adsense-loaded');
        containerRef.current?.classList.add('adsense-failed');
        return;
      }

      if (
        insRef.current &&
        insRef.current.childElementCount > 0 &&
        containerRef.current
      ) {
        loaded = true;
        containerRef.current.classList.remove('adsense-failed');
        containerRef.current.classList.add('adsense-loaded');
      }
    });

    observer.observe(insRef.current, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-ad-status'],
    });

    // 5초 내 로드 안 되면 숨김
    const timeout = setTimeout(() => {
      if (!loaded && containerRef.current) {
        containerRef.current.classList.add('adsense-failed');
        observer.disconnect();
      }
    }, 5000);

    return () => {
      observer.disconnect();
      clearTimeout(timeout);
    };
  }, [isVisible]);

  if (!isEnabled && !IS_DEV) return null;

  // 로컬 개발 환경: 미리보기 플레이스홀더
  if (IS_DEV) {
    return (
      <div ref={containerRef} css={devPreviewCSS}>
        <div className="dev-ad-icon">AD</div>
        <div className="dev-ad-content">
          <div className="dev-ad-label">AdSense InFeed · {unitKey}</div>
          <div className="dev-ad-title">광고 제목이 여기에 표시됩니다</div>
          <div className="dev-ad-body">광고 설명 텍스트 미리보기</div>
        </div>
        <button type="button" className="dev-ad-cta">
          자세히
        </button>
      </div>
    );
  }

  return (
    <div ref={containerRef} css={containerCSS}>
      <div css={adLabelCSS}>광고</div>
      {isVisible && adUnit && (
        <ins
          ref={insRef}
          className="adsbygoogle"
          style={{ display: 'block' }}
          data-ad-format="fluid"
          data-ad-layout-key={adUnit.layoutKey}
          data-ad-client={ADSENSE_CLIENT}
          data-ad-slot={adUnit.slot}
        />
      )}
    </div>
  );
};

export default NativeInFeedAd;
